'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';
import { ProductLogoMediumPlaces } from '@/components/Logo';
import { Text } from '@/components/Typography';
import { defaultNS, useTranslation } from '@/lib/i18n';
import type { MemberDevice, MemberEquipment, PortalAssetStatus, PortalPlace } from '@/mocks/db/types';
import { ledgerColumn } from '../-shared/-components/ledgerColumn';
import { LedgerContent } from '../-shared/-components/LedgerContent';
import { LedgerDataTable } from '../-shared/-components/LedgerDataTable';
import { type LedgerFilterTab, LedgerFilterTabs } from '../-shared/-components/LedgerFilterTabs';
import { LedgerHeader } from '../-shared/-components/LedgerHeader';
import { AssetStatusBadge, PersonCell } from '../-shared/-components/ResourceCells';
import { useMemberDevices, useMemberEquipments, usePortalPersons, usePortalPlaces } from '../-shared/-query';
import { toMapById } from '../-shared/-utils';
const PLACES_PATH = '/individual/portal/lookup/places';
const LOOKUP_PATH = '/individual/portal/lookup';
const placesSearchSchema = z.object({
  scope: z.enum(['managed', 'all']).catch('managed'),
  id: z.string().optional(),
  tab: z.enum(['devices', 'equipments']).catch('devices')
});
export const Route = createFileRoute('/_individual/individual/portal/lookup/_ledger/places/')({
  validateSearch: placesSearchSchema,
  component: PlacesLookupPage
});
function devicesAtPlace(devices: readonly MemberDevice[], placeId: string): MemberDevice[] {
  return devices.filter(d => d.installPlaceId === placeId || d.storagePlaceId === placeId);
}
function equipmentsAtPlace(equipments: readonly MemberEquipment[], placeId: string): MemberEquipment[] {
  return equipments.filter(e => e.placeId === placeId);
}
function PlacesLookupPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.lookupResources'
  });
  const {
    t: ct
  } = useTranslation(defaultNS, {
    keyPrefix: 'common.breadcrumbs'
  });
  const {
    t: th
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.home'
  });
  const {
    scope,
    id,
    tab
  } = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    data: places,
    isPending
  } = usePortalPlaces();
  const {
    data: devices
  } = useMemberDevices();
  const {
    data: equipments
  } = useMemberEquipments();
  const {
    data: persons
  } = usePortalPersons();
  const personsById = useMemo(() => toMapById(persons), [persons]);
  const allDevices = devices ?? [];
  const allEquipments = equipments ?? [];
  const selected = id != null ? places?.find(p => p.id === id) ?? null : null;
  const rows = useMemo(() => {
    const all = places ?? [];
    return scope === 'managed' ? all.filter(p => p.managedByMe) : all;
  }, [places, scope]);
  const deviceStatusLabel = (status: PortalAssetStatus) => status === 'assigned' ? t('devices.status.assigned') : t('devices.status.installed');
  const equipmentStatusLabel = (status: PortalAssetStatus) => status === 'assigned' ? t('equipments.status.assigned') : t('equipments.status.placed');
  const empty = <Text variant="p" className="text-neutral-muted-on-fill">
      {t('common.empty')}
    </Text>;
  if (selected != null) {
    const placeDevices = devicesAtPlace(allDevices, selected.id);
    const placeEquipments = equipmentsAtPlace(allEquipments, selected.id);
    const detailTabs: LedgerFilterTab<'devices' | 'equipments'>[] = [{
      value: 'devices',
      label: `${t('places.detail.devicesTab')}（${placeDevices.length}）`
    }, {
      value: 'equipments',
      label: `${t('places.detail.equipmentsTab')}（${placeEquipments.length}）`
    }];
    const detailFilterTabs = <LedgerFilterTabs tabs={detailTabs} value={tab} onSelect={next => void navigate({
      search: prev => ({
        ...prev,
        tab: next
      })
    })} ariaLabel={t('common.filterTabsLabel')} />;
    const deviceColumns = [ledgerColumn<MemberDevice>({
      id: 'name',
      header: t('devices.headers.name'),
      cell: d => <Text variant="label">{d.name}</Text>
    }), ledgerColumn<MemberDevice>({
      id: 'category',
      header: t('devices.headers.category'),
      cell: d => d.category
    }), ledgerColumn<MemberDevice>({
      id: 'assetTag',
      header: t('devices.headers.assetTag'),
      cell: d => d.assetTag
    }), ledgerColumn<MemberDevice>({
      id: 'status',
      header: t('devices.headers.status'),
      cell: d => <AssetStatusBadge status={d.status} label={deviceStatusLabel(d.status)} />
    }), ledgerColumn<MemberDevice>({
      id: 'holder',
      header: t('devices.headers.holder'),
      cell: d => <PersonCell person={d.holderPersonId != null ? personsById.get(d.holderPersonId) : null} />
    })];
    const equipmentColumns = [ledgerColumn<MemberEquipment>({
      id: 'name',
      header: t('equipments.headers.name'),
      cell: e => <Text variant="label">{e.name}</Text>
    }), ledgerColumn<MemberEquipment>({
      id: 'category',
      header: t('equipments.headers.category'),
      cell: e => e.category
    }), ledgerColumn<MemberEquipment>({
      id: 'assetTag',
      header: t('equipments.headers.assetTag'),
      cell: e => e.assetTag
    }), ledgerColumn<MemberEquipment>({
      id: 'status',
      header: t('equipments.headers.status'),
      cell: e => <AssetStatusBadge status={e.status} label={equipmentStatusLabel(e.status)} />
    }), ledgerColumn<MemberEquipment>({
      id: 'holder',
      header: t('equipments.headers.holder'),
      cell: e => <PersonCell person={e.holderPersonId != null ? personsById.get(e.holderPersonId) : null} />
    })];
    return <>
        <LedgerHeader breadcrumbs={[{
        label: ct('home'),
        link: {
          to: '/'
        }
      }, {
        label: th('lookup.title'),
        link: {
          to: LOOKUP_PATH
        }
      }, {
        label: t('places.title'),
        link: {
          to: PLACES_PATH,
          search: {
            scope
          }
        }
      }, {
        label: selected.name
      }]} Logo={ProductLogoMediumPlaces} title={selected.name} subtitle={selected.address} />
        <LedgerContent>
          {tab === 'devices' ? <LedgerDataTable<MemberDevice> columns={deviceColumns} data={placeDevices} isPending={isPending} filterTabs={detailFilterTabs} getRowId={d => d.id} getSearchText={d => `${d.name} ${d.category} ${d.assetTag}`} searchPlaceholder={t('common.search')} emptyContent={empty} /> : <LedgerDataTable<MemberEquipment> columns={equipmentColumns} data={placeEquipments} isPending={isPending} filterTabs={detailFilterTabs} getRowId={e => e.id} getSearchText={e => `${e.name} ${e.category} ${e.assetTag}`} searchPlaceholder={t('common.search')} emptyContent={empty} />}
        </LedgerContent>
      </>;
  }
  const columns = [ledgerColumn<PortalPlace>({
    id: 'name',
    header: t('places.headers.name'),
    cell: p => <Text variant="label">{p.name}</Text>
  }), ledgerColumn<PortalPlace>({
    id: 'address',
    header: t('places.headers.address'),
    cell: p => <Text variant="tableItem" className="text-neutral-muted-on-fill">
          {p.address}
        </Text>
  }), ledgerColumn<PortalPlace>({
    id: 'manager',
    header: t('places.headers.manager'),
    cell: p => <PersonCell person={p.managerPersonId != null ? personsById.get(p.managerPersonId) : null} />
  }), ledgerColumn<PortalPlace>({
    id: 'deviceCount',
    header: t('places.headers.deviceCount'),
    align: 'right',
    width: '6rem',
    cell: p => devicesAtPlace(allDevices, p.id).length
  }), ledgerColumn<PortalPlace>({
    id: 'equipmentCount',
    header: t('places.headers.equipmentCount'),
    align: 'right',
    width: '6rem',
    cell: p => equipmentsAtPlace(allEquipments, p.id).length
  })];
  const scopeTabs: LedgerFilterTab<'managed' | 'all'>[] = [{
    value: 'managed',
    label: t('places.tabs.managed')
  }, {
    value: 'all',
    label: t('places.tabs.all')
  }];
  return <>
      <LedgerHeader breadcrumbs={[{
      label: ct('home'),
      link: {
        to: '/'
      }
    }, {
      label: th('lookup.title'),
      link: {
        to: LOOKUP_PATH
      }
    }, {
      label: t('places.title')
    }]} Logo={ProductLogoMediumPlaces} title={t('places.title')} />
      <LedgerContent className="h-full">
        <LedgerDataTable<PortalPlace> fillHeight columns={columns} data={rows} isPending={isPending} filterTabs={<LedgerFilterTabs tabs={scopeTabs} value={scope} onSelect={s => void navigate({
        search: prev => ({
          ...prev,
          scope: s
        })
      })} ariaLabel={t('common.filterTabsLabel')} />} getRowId={p => p.id} getSearchText={p => {
        const manager = p.managerPersonId != null ? personsById.get(p.managerPersonId)?.name ?? '' : '';
        return `${p.name} ${p.address} ${manager}`;
      }} searchPlaceholder={t('common.search')} onRowSelect={p => void navigate({
        search: prev => ({
          ...prev,
          id: p.id
        })
      })} emptyContent={empty} />
      </LedgerContent>
    </>;
}